import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Cookie } from "lucide-react";

const KEY = "rai_cookie_consent";

function genId() {
  return "cc_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function loadConsent() {
  try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch { return null; }
}

// Global opener so the footer button can re-open preferences.
let _opener = null;
export function openCookiePreferences() { if (_opener) _opener(); }

export default function CookieBanner() {
  const { lang } = useApp();
  const L = (it, en) => (lang === "en" ? en : it);
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, statistics: false, marketing: false });
  const [cookieVersion, setCookieVersion] = useState(null);

  useEffect(() => {
    api.get("/legal/cookie").then((r) => setCookieVersion(r.data.version)).catch(() => {});
    const saved = loadConsent();
    if (!saved) { setVisible(true); }
    else if (saved.preferences) { setPrefs(saved.preferences); }
  }, []);

  const open = useCallback(() => { const s = loadConsent(); if (s?.preferences) setPrefs(s.preferences); setShowPrefs(true); setVisible(true); }, []);
  useEffect(() => { _opener = open; return () => { _opener = null; }; }, [open]);

  const persist = async (p) => {
    const consent_id = loadConsent()?.consent_id || genId();
    const record = { consent_id, preferences: p, cookie_version: cookieVersion, accepted_at: new Date().toISOString() };
    localStorage.setItem(KEY, JSON.stringify(record));
    setPrefs(p); setVisible(false); setShowPrefs(false);
    try { await api.post("/cookie-consent", { consent_id, preferences: p }); } catch {}
  };

  const acceptAll = () => persist({ necessary: true, statistics: true, marketing: true });
  const rejectAll = () => persist({ necessary: true, statistics: false, marketing: false });
  const saveCustom = () => persist({ ...prefs, necessary: true });

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4" data-testid="cookie-banner">
      <div className="max-w-3xl mx-auto rounded-2xl border border-border bg-background shadow-2xl p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <Cookie className="w-6 h-6 text-primary shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold mb-1">{L("Rispettiamo la tua privacy", "We respect your privacy")}</h3>
            <p className="text-sm text-muted-foreground">
              {L("Utilizziamo cookie necessari al funzionamento e, con il tuo consenso, cookie statistici e di marketing. ",
                 "We use cookies necessary for operation and, with your consent, statistics and marketing cookies. ")}
              <Link to="/cookie-policy" className="text-primary hover:underline">Cookie Policy</Link>
            </p>

            {showPrefs && (
              <div className="mt-4 space-y-2" data-testid="cookie-prefs">
                {[
                  ["necessary", L("Necessari (sempre attivi)", "Necessary (always on)"), true],
                  ["statistics", L("Statistici", "Statistics"), false],
                  ["marketing", L("Marketing", "Marketing"), false],
                ].map(([k, label, locked]) => (
                  <label key={k} className="flex items-center justify-between text-sm border border-border rounded-lg px-3 py-2">
                    <span>{label}</span>
                    <input type="checkbox" data-testid={`cookie-pref-${k}`} disabled={locked}
                      checked={locked ? true : !!prefs[k]}
                      onChange={(e) => setPrefs({ ...prefs, [k]: e.target.checked })} />
                  </label>
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-2 mt-4">
              <Button size="sm" data-testid="cookie-accept-all" onClick={acceptAll}>{L("Accetta tutti", "Accept all")}</Button>
              <Button size="sm" variant="outline" data-testid="cookie-reject" onClick={rejectAll}>{L("Rifiuta i non necessari", "Reject non-essential")}</Button>
              {showPrefs
                ? <Button size="sm" variant="outline" data-testid="cookie-save" onClick={saveCustom}>{L("Salva preferenze", "Save preferences")}</Button>
                : <Button size="sm" variant="ghost" data-testid="cookie-customize" onClick={() => setShowPrefs(true)}>{L("Personalizza", "Customize")}</Button>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
